import { API, Auth } from 'aws-amplify';
import React, { createContext, useContext, useEffect } from 'react';

import { GetTenants } from './controller/controller';
import { User, Driver, OrderType, Unit, BoxType, Facility, Admin, Appointment, Lead, LedgerEntry } from './model/types';
import { listDrivers, ordersbyFacility, getAdmin, unitsByFacility, boxesByFacility, appointmentsByFacility, listUnits, leadsByFacility, ledgerByFacility } from '../graphql/queries';
import { GetAdminQueryResult, Facilities } from '../graphql/types';
//import { GetAvailableUnits } from '../utils/sitelink/sitelink';
import { getAppointments } from '../utils/getAppointments';
import { SitelinkTableEntry, SitelinkUnit } from '../utils/sitelink/types';

type UsersContextType = {
  users: User[];
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  drivers: Driver[];
  setDrivers: React.Dispatch<React.SetStateAction<Driver[]>>;
  orders: OrderType[];
  setOrders: React.Dispatch<React.SetStateAction<OrderType[]>>;
  facilityInfo: Facility | undefined;
  setFacility: React.Dispatch<React.SetStateAction<Facility | undefined>>;
  admin: Admin[];
  setAdmin: React.Dispatch<React.SetStateAction<Admin[]>>;
  units: Unit[];
  setUnits: React.Dispatch<React.SetStateAction<Unit[]>>;
  // Units == DynamoDB, sitelinkUnits == Sitelink DB
  sitelinkUnits: SitelinkTableEntry<SitelinkUnit>[],
  setSitelinkUnits: React.Dispatch<React.SetStateAction<SitelinkTableEntry<SitelinkUnit>[]>>
  boxes: BoxType[];
  setBoxes: React.Dispatch<React.SetStateAction<BoxType[]>>;
  appointments: Appointment[]; // Deprecated
  setAppointments: React.Dispatch<React.SetStateAction<Appointment[]>>; // Deprecated
  appointments2: any[];
  setAppointments2: React.Dispatch<React.SetStateAction<any[]>>;
  leads: Lead[];
  setLeads: React.Dispatch<React.SetStateAction<Lead[]>>;
  ledgerEntries: LedgerEntry[];
  setLedgerEntries: React.Dispatch<React.SetStateAction<LedgerEntry[]>>;
};

const UsersContext = createContext<UsersContextType>(undefined!);

// Convert AWS Facility into AdminApp Facility type
const appsyncFacilityToFacilityType = (input: Facilities): Facility | undefined => {
  if (!input) return undefined;
  const facility = input.items[0];
  if (facility === null || !facility.name || !facility.address || !facility.movingCosts?.flatFee || !facility.movingCosts.hourlyFee) {
    return undefined;
  }

  return {
    id: facility.id,
    name: facility.name,
    address: facility.address,
    movingCosts: {
      flatFee: facility.movingCosts?.flatFee,
      hourlyFee: facility.movingCosts?.hourlyFee,
    },
  };
}

export const UsersWrapper: React.FC = ({ children }) => {
  const [users, setUsers] = React.useState<User[]>([]);
  const [drivers, setDrivers] = React.useState<Driver[]>([]);
  const [orders, setOrders] = React.useState<OrderType[]>([]);
  const [facilityInfo, setFacility] = React.useState<Facility | undefined>(undefined);
  const [admin, setAdmin] = React.useState<Admin[]>([]);
  const [units, setUnits] = React.useState<Unit[]>([]);
  const [boxes, setBoxes] = React.useState<BoxType[]>([]);
  const [appointments, setAppointments] = React.useState<Appointment[]>([]); // Deprecated
  const [appointments2, setAppointments2] = React.useState<any[]>([]);
  const [leads, setLeads] = React.useState<Lead[]>([]);
  const [ledgerEntries, setLedgerEntries] = React.useState<LedgerEntry[]>([]);
  const [sitelinkUnits, setSitelinkUnits] = React.useState<SitelinkTableEntry<SitelinkUnit>[]>([]);
  const value = ' ';

  // TODO(sitelink): input should not be any type.
  // TODO(cleanup): maybe this should be an async fun and not littered with .then().then().then()... chains, but rather awaits and resolveAll's
  
  async function forceLogoutAndRedirect() {
    try {
      await Auth.signOut();
    } catch (e) {
      console.error('Error during sign out:', e);
    }
    // choose where you want them to land:
    window.location.assign('/admin');
    // or: window.location.assign('/');
  }
  
  async function fetchFacility(awsFacilities: Facilities) {
    const facility = appsyncFacilityToFacilityType(awsFacilities)
    if (!facility) {
      await forceLogoutAndRedirect();
      return;
    }
    setFacility(facility);
    const facilityID = facility.id;
    setUsers(await GetTenants(facilityID))
    // ordersbyFacility is a GSI query (not a filtered scan) — high limit so we
    // get every order in one page instead of silently capping at ~100.
    const orderList = API.graphql({query: ordersbyFacility, variables: {facilityID: facilityID, limit: 10000}}) as Promise<any>;
    orderList.then(({ data: { ordersbyFacility: { items } } } ) => setOrders(items));
    const driverList = API.graphql({query: listDrivers, variables: {filter: {facilityID: {eq: facilityID}}}}) as Promise<any>;
    driverList.then(({ data: { listDrivers: { items } } } ) => setDrivers(items));
    const leadList = API.graphql({query: leadsByFacility, variables: {facilityID: facilityID, limit: 1000}}) as Promise<any>;
    leadList.then(({ data: { leadsByFacility: { items } } }) => setLeads(items));
    const ledgerList = API.graphql({query: ledgerByFacility, variables: {facilityID: facilityID, limit: 10000}}) as Promise<any>;
    ledgerList.then(({ data: { ledgerByFacility: { items } } }) => setLedgerEntries(items));
    // eslint-disable-next-line
    const boxList = API.graphql({query: boxesByFacility, variables: {facilityID: facilityID, limit: 10000}}) as Promise<any>;
    boxList.then(({ data: { boxesByFacility: { items } } } ) => setBoxes(items));
    // eslint-disable-next-line
    const unitList = API.graphql({query: unitsByFacility, variables: {facilityID: facilityID}}) as Promise<any>;
    unitList.then(({ data: { unitsByFacility: { items } } } ) => setUnits(items));
    // eslint-disable-next-line
    const appointmentList = API.graphql({query: appointmentsByFacility, variables: {facilityID: facilityID}}) as Promise<any>; // Deprecated
    appointmentList.then(({ data: { appointmentsByFacility: { items } } } ) => {setAppointments(items); console.log("Fetched Appointments:", items);}); // Deprecated

    const groupAppointmentsByDate = (appointments) => {
      return appointments.reduce((acc, appointment) => {
        const date = appointment.date; // Expecting appointment.date to be in YYYY-MM-DD format
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(appointment);
        return acc;
      }, {});
    };

    const fetchAppointments = async () => {
      try {
        const data = await getAppointments(); // Modify this based on your API logic if necessary
        const appointmentsData = JSON.parse(data.body); // Ensure parsed data if necessary
        const filteredAppointmentsData = appointmentsData.filter((appointment: any) => 
          appointment.facilityID === facilityID
        );
        const groupedAppointments = groupAppointmentsByDate(filteredAppointmentsData);

        setAppointments2(groupedAppointments);
        // setAppointments(groupedAppointments); // Update state with grouped appointments
      } catch (err) {
        console.log(err);
      }
    };
    await fetchAppointments(); // Execute the fetchAppointments function

    // removed sitelink GetAvailableUnits
    {/*const fetchedSitelinkUnits = await GetAvailableUnits();
      // alert(fetchedSitelinkUnits.map(unit => unit.Table.UnitID));
      setSitelinkUnits(fetchedSitelinkUnits);
    } catch (e: unknown) {
      alert('Unable to fetch sitelink units. Please try again.');
      setSitelinkUnits([]);
    }
    */}

  }

    useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const currentUserInfo = await Auth.currentAuthenticatedUser();
        const userEmail = currentUserInfo.attributes.email;

        const adminInfo = await API.graphql({
          query: getAdmin,
          variables: { id: userEmail },
        }) as GetAdminQueryResult;

        const adminRecord = adminInfo.data.getAdmin;

        if (!adminRecord || !adminRecord.facility) {
          // user is authenticated but not an admin
          await forceLogoutAndRedirect();
          return;
        }

        await fetchFacility(adminRecord.facility);
      } catch (error) {
        console.error('Error fetching admin:', error);
        // treat this as "not an admin" too
        await forceLogoutAndRedirect();
      }
    };

    fetchAdmin();
    // eslint-disable-next-line
  }, [value]);

  const defaultValue = { users, setUsers, drivers, setDrivers, orders, setOrders, facilityInfo, setFacility, admin, setAdmin, units, setUnits, boxes, setBoxes, appointments, setAppointments, sitelinkUnits, setSitelinkUnits, appointments2, setAppointments2, leads, setLeads, ledgerEntries, setLedgerEntries };

  return (
    <UsersContext.Provider value={defaultValue}>
      {children}
    </UsersContext.Provider>
  );
};

export const useUsers = (): UsersContextType => useContext(UsersContext);
